export interface ForecastPeriod {
  dt: number;
  time: string;
  temp: number;
  rainMm: number;
  snowMm: number;
  pop: number;
  condition: string;
  description: string;
  icon: string;
}

export interface ForecastData {
  periods: ForecastPeriod[];
  totalRainMm: number;
  totalSnowMm: number;
  totalRainIn: number;
  totalSnowIn: number;
  maxPop: number;
  description: string;
}

export interface WeatherData {
  temperature: number;
  condition: string;
  icon: string;
  city: string;
  state: string;
  error?: string;
  // Extended fields for modal display
  feels_like?: number;
  temp_min?: number;
  temp_max?: number;
  pressure?: number;
  humidity?: number;
  visibility?: number;
  wind_speed?: number;
  wind_deg?: number;
  wind_gust?: number;
  clouds?: number;
  description?: string;
}

interface OpenWeatherForecastItem {
  dt: number;
  main: {
    temp: number;
  };
  weather: Array<{
    main: string;
    description: string;
    icon: string;
  }>;
  rain?: { '3h'?: number };
  snow?: { '3h'?: number };
  pop: number;
  dt_txt: string;
}

interface OpenWeatherForecastResponse {
  list: OpenWeatherForecastItem[];
  city: {
    name: string;
    timezone: number;
  };
}

interface OpenWeatherResponse {
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    humidity: number;
    sea_level?: number;
    grnd_level?: number;
  };
  weather: Array<{
    main: string;
    description: string;
    icon: string;
  }>;
  visibility: number;
  wind: {
    speed: number;
    deg: number;
    gust?: number;
  };
  clouds: {
    all: number;
  };
  name: string;
  sys: {
    country: string;
  };
}

// Parse city and state from address string
function parseLocationFromAddress(address: string): { city: string; state: string; zip: string } {
  if (!address) return { city: '', state: '', zip: '' };

  console.log('[Weather] Parsing address:', address);

  // Common address formats:
  // "123 Main St, Springfield, IL 62701"
  // "456 Oak Ave, Denver, Colorado 80202"

  const parts = address.split(',').map(part => part.trim());
  console.log('[Weather] Address parts:', parts);

  if (parts.length >= 3) {
    const [, city, stateZip ] = parts;
    const [state, zip] = stateZip.split(' ');
    console.log('[Weather] Parsed zip:', zip);
    return { city, state, zip };
  }

  console.log('[Weather] Could not parse address - insufficient parts');
  return { city: '', state: '', zip: '' };
}

// Convert OpenWeatherMap condition to user-friendly text
function formatCondition(weatherMain: string, description: string): string {
  const conditionMap: Record<string, string> = {
    'Clear': 'Sunny',
    'Clouds': 'Cloudy',
    'Rain': 'Rainy',
    'Drizzle': 'Drizzle',
    'Thunderstorm': 'Stormy',
    'Snow': 'Snowy',
    'Mist': 'Misty',
    'Fog': 'Foggy',
    'Haze': 'Hazy',
  };

  return conditionMap[weatherMain] || description;
}

// Fetch weather data for a given city/state
export async function fetchWeatherByLocation(city: string, state: string, zip: string): Promise<WeatherData> {
  try {
    // Using OpenWeatherMap free tier - you'd need to add API key to env vars
    const API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;

    if (!API_KEY) {
      console.warn('OpenWeatherMap API key not configured');
      return {
        temperature: 72,
        condition: 'Sunny',
        icon: '01d',
        city: city || 'Unknown',
        state: state || 'Unknown',
        error: 'Weather API not configured',
      };
    }

    const response = await fetch(
      `https://api.openweathermap.org/data/2.5/weather?zip=${zip}&appid=${API_KEY}&units=imperial`,
    );

    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }

    const data: OpenWeatherResponse = await response.json();

    return {
      temperature: Math.round(data.main.temp),
      condition: formatCondition(data.weather[0].main, data.weather[0].description),
      icon: data.weather[0].icon,
      city: data.name,
      state: state || data.sys.country,
      // Extended fields
      feels_like: Math.round(data.main.feels_like),
      temp_min: Math.round(data.main.temp_min),
      temp_max: Math.round(data.main.temp_max),
      pressure: data.main.pressure,
      humidity: data.main.humidity,
      visibility: data.visibility,
      wind_speed: data.wind.speed,
      wind_deg: data.wind.deg,
      wind_gust: data.wind.gust,
      clouds: data.clouds.all,
      description: data.weather[0].description,
    };
  } catch (error) {
    console.error('Error fetching weather:', error);
    return {
      temperature: 72,
      condition: 'Sunny',
      icon: '01d',
      city: city || 'Unknown',
      state: state || 'Unknown',
      error: 'Unable to fetch weather',
    };
  }
}

// Fetch weather data based on company address
export async function fetchWeatherByAddress(address: string): Promise<WeatherData> {
  const { city, state, zip } = parseLocationFromAddress(address);

  if (!city) {
    return {
      temperature: 72,
      condition: 'Sunny',
      icon: '01d',
      city: 'Unknown',
      state: 'Unknown',
      error: 'No city found in address',
    };
  }

  return fetchWeatherByLocation(city, state, zip);
}

const MM_TO_INCHES = 0.0393701;

function formatTime3h(dtTxt: string): string {
  // dtTxt is "2024-01-01 15:00:00" in UTC
  const date = new Date(dtTxt.replace(' ', 'T') + 'Z');
  return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
}

function generateForecastDescription(forecast: ForecastData, currentCondition: string): string {
  const { totalRainIn, totalSnowIn, maxPop, periods } = forecast;
  const hasPrecip = totalRainIn > 0.01 || totalSnowIn > 0.01;
  const highPop = maxPop >= 0.6;

  if (!hasPrecip && !highPop) {
    if (currentCondition.toLowerCase().includes('sunny') || currentCondition.toLowerCase().includes('clear')) {
      return 'Clear skies expected over the next 24 hours. No precipitation in the forecast.';
    }
    return 'No precipitation expected over the next 24 hours.';
  }

  const parts: string[] = [];

  if (totalSnowIn >= 0.1) {
    const inchesStr = totalSnowIn >= 1 ? `${totalSnowIn.toFixed(1)}"` : `${totalSnowIn.toFixed(2)}"`;
    parts.push(`${inchesStr} of snow expected`);
  }
  if (totalRainIn >= 0.01) {
    const inchesStr = totalRainIn >= 1 ? `${totalRainIn.toFixed(1)}"` : `${totalRainIn.toFixed(2)}"`;
    parts.push(`${inchesStr} of rain expected`);
  }

  // Find when precipitation starts
  const firstPrecipPeriod = periods.find(p => p.rainMm > 0 || p.snowMm > 0);
  if (firstPrecipPeriod) {
    parts.push(`starting around ${firstPrecipPeriod.time}`);
  }

  if (highPop) {
    parts.push(`up to ${Math.round(maxPop * 100)}% chance`);
  }

  return parts.join(', ') + '.';
}

// Fetch forecast data for a given zip code
async function fetchForecastByLocation(zip: string): Promise<ForecastData | null> {
  try {
    const API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;
    if (!API_KEY) return null;

    const response = await fetch(
      `https://api.openweathermap.org/data/2.5/forecast?zip=${zip}&appid=${API_KEY}&units=imperial&cnt=8`,
    );

    if (!response.ok) return null;

    const data: OpenWeatherForecastResponse = await response.json();
    const periods: ForecastPeriod[] = data.list.map(item => ({
      dt: item.dt,
      time: formatTime3h(item.dt_txt),
      temp: Math.round(item.main.temp),
      rainMm: item.rain?.['3h'] ?? 0,
      snowMm: item.snow?.['3h'] ?? 0,
      pop: item.pop,
      condition: item.weather[0].main,
      description: item.weather[0].description,
      icon: item.weather[0].icon,
    }));

    const totalRainMm = periods.reduce((sum, p) => sum + p.rainMm, 0);
    const totalSnowMm = periods.reduce((sum, p) => sum + p.snowMm, 0);
    const maxPop = Math.max(...periods.map(p => p.pop));

    return {
      periods,
      totalRainMm,
      totalSnowMm,
      totalRainIn: totalRainMm * MM_TO_INCHES,
      totalSnowIn: totalSnowMm * MM_TO_INCHES,
      maxPop,
      description: '', // filled in after we have current condition
    };
  } catch (error) {
    console.error('Error fetching forecast:', error);
    return null;
  }
}

// Cache weather data to avoid excessive API calls
class WeatherCache {
  private cache = new Map<string, { data: WeatherData; timestamp: number }>();
  private forecastCache = new Map<string, { data: ForecastData; timestamp: number }>();
  private CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  get(key: string): WeatherData | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  set(key: string, data: WeatherData): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  getForecast(key: string): ForecastData | null {
    const cached = this.forecastCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    this.forecastCache.delete(key);
    return null;
  }

  setForecast(key: string, data: ForecastData): void {
    this.forecastCache.set(key, { data, timestamp: Date.now() });
  }
}

const weatherCache = new WeatherCache();

// Main function to get weather with caching
export async function getWeatherForAddress(address: string): Promise<WeatherData> {
  const cacheKey = address.toLowerCase().trim();

  // Check cache first
  const cached = weatherCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch fresh data
  const weatherData = await fetchWeatherByAddress(address);

  // Cache the result
  weatherCache.set(cacheKey, weatherData);

  return weatherData;
}

// Main function to get forecast with caching
export async function getForecastForAddress(address: string, currentCondition: string): Promise<ForecastData | null> {
  const { zip } = parseLocationFromAddress(address);
  if (!zip) return null;

  const cacheKey = address.toLowerCase().trim() + ':forecast';
  const cached = weatherCache.getForecast(cacheKey);
  if (cached) return cached;

  const forecast = await fetchForecastByLocation(zip);
  if (!forecast) return null;

  forecast.description = generateForecastDescription(forecast, currentCondition);
  weatherCache.setForecast(cacheKey, forecast);
  return forecast;
}
